import React, { useEffect, useRef, useState } from "react";
import { ownerNameError } from "@/lib/ownerName";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserCog, Upload, Plus, Trash2, Eraser, PenLine, Image as ImageIcon, ListOrdered } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

export default function ProfileTab({ owner }) {
  const { setOwner } = useAuth();
  const [form, setForm] = useState({ ...owner, terms: owner?.terms?.length ? owner.terms : [""] });
  const [busy, setBusy] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const [sigUrl, setSigUrl] = useState(null);
  const logoInputRef = useRef(null);
  const sigInputRef = useRef(null);

  useEffect(() => {
    let alive = true;
    const fetchImg = async (kind, setter) => {
      try {
        const r = await api.get(`/owners/me/${kind}`, { responseType: "blob" });
        if (alive) setter(URL.createObjectURL(r.data));
      } catch { /* not uploaded yet */ }
    };
    fetchImg("logo", setLogoUrl);
    fetchImg("signature", setSigUrl);
    return () => { alive = false; };
  }, []);

  const uploadImage = async (kind, file) => {
    const limitMb = kind === "logo" ? 2 : 1;
    const label = kind === "logo" ? "Logo" : "Signature";
    if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) return toast.error("Only PNG, JPG or WEBP images are allowed");
    if (file.size > limitMb * 1024 * 1024) return toast.error(`${label} image must be under ${limitMb}MB`);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/owners/me/${kind}`, fd);
      toast.success(kind === "logo" ? "Logo saved" : "Signature image saved");
      const r = await api.get(`/owners/me/${kind}`, { responseType: "blob" });
      (kind === "logo" ? setLogoUrl : setSigUrl)(URL.createObjectURL(r.data));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    }
  };

  const removeImage = async (kind) => {
    try {
      await api.delete(`/owners/me/${kind}`);
      (kind === "logo" ? setLogoUrl : setSigUrl)(null);
      toast.success(kind === "logo" ? "Logo removed" : "Signature removed");
    } catch {
      toast.error("Failed to remove");
    }
  };

  const setTerm = (i, v) => setForm((f) => ({ ...f, terms: f.terms.map((t, idx) => (idx === i ? v : t)) }));
  const addTerm = () => setForm((f) => ({ ...f, terms: [...f.terms, ""] }));
  const removeTerm = (i) => setForm((f) => ({ ...f, terms: f.terms.filter((_, idx) => idx !== i) }));

  const saveBeds = async () => {
    const total = parseInt(form.total_beds, 10);
    const avail = parseInt(form.available_beds, 10);
    if (!(total >= 1 && total <= 300)) return toast.error("Total beds must be between 1 and 300");
    if (!(avail >= 0 && avail <= 50)) return toast.error("Available beds must be between 0 and 50");
    if (avail > total) return toast.error("Available beds cannot exceed total beds");
    setBusy(true);
    try {
      await api.put("/owners/me", { total_beds: total, available_beds: avail });
      toast.success("Beds updated");
    } catch (e) { toast.error(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Could not update beds"); }
    finally { setBusy(false); }
  };

  const saveOwnerName = async () => {
    const err = ownerNameError(form.owner_name);
    if (err) return toast.error(err);
    setBusy(true);
    try {
      await api.put("/owners/me", { owner_name: (form.owner_name || "").trim() });
      toast.success("Owner's name saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save owner's name"); }
    finally { setBusy(false); }
  };

  const saveTerms = async () => {
    setBusy(true);
    try {
      const r = await api.put("/owners/me", { terms: (form.terms || []).map((t) => t.trim()).filter(Boolean) });
      setOwner(r.data);
      toast.success("Terms & conditions saved");
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (Array.isArray(d) ? d[0]?.msg : "Update failed"));
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl space-y-6" data-testid="profile-tab">
      {/* Branding: logo + e-signature */}
      <Card className="p-6 border-slate-200">
        <h2 className="font-display text-xl font-semibold flex items-center gap-2"><ImageIcon className="h-5 w-5 text-indigo-600" /> Branding</h2>
        <p className="text-sm text-slate-500 mt-0.5">Your logo and e-signature appear on every receipt PDF.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Property Logo</p>
            <div className="h-24 w-24 rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden mb-3 relative group" data-testid="profile-logo-preview">
              {logoUrl ? (
                <>
                  <img src={logoUrl} alt="Logo" className="h-full w-full object-contain" />
                  <button
                    onClick={() => removeImage("logo")}
                    title="Remove logo"
                    className="absolute top-1 right-1 bg-white/90 hover:bg-rose-50 text-rose-600 rounded p-1 shadow"
                    data-testid="profile-logo-remove-button"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </>
              ) : (
                <ImageIcon className="h-8 w-8 text-slate-300" />
              )}
            </div>
            <input type="file" accept="image/png,image/jpeg,image/webp" ref={logoInputRef} className="hidden" onChange={(e) => e.target.files?.[0] && uploadImage("logo", e.target.files[0])} data-testid="profile-logo-file-input" />
            <Button variant="outline" size="sm" onClick={() => logoInputRef.current?.click()} data-testid="profile-logo-upload-button">
              <Upload className="h-4 w-4 mr-1.5" /> Upload logo
            </Button>
            <p className="text-[11px] text-slate-500 mt-1.5 font-medium">PNG, JPG or WEBP · <span className="text-indigo-600 font-semibold">Max size: 2MB</span></p>
          </div>

          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Owner's E-Signature</p>
            
            {sigUrl && (
              <div className="mb-3 flex items-center gap-3">
                <div className="h-14 p-1 rounded border border-slate-200 bg-white flex items-center" data-testid="profile-signature-preview">
                  <img src={sigUrl} alt="Signature" className="h-full object-contain" />
                </div>
                <Button variant="ghost" size="sm" className="text-rose-600 hover:bg-rose-50 text-xs" onClick={() => removeImage("signature")} data-testid="profile-signature-remove-button">
                  <Trash2 className="h-3.5 w-3.5 mr-1" /> Remove
                </Button>
              </div>
            )}

            {/* Option 1: Draw on smart screen signature pad */}
            <SignaturePad onSave={(blob) => uploadImage("signature", new File([blob], "signature.png", { type: "image/png" }))} />

            <div className="relative my-3 text-center">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200" /></div>
              <span className="relative bg-white px-2 text-[10px] font-semibold uppercase text-slate-400">or upload an image</span>
            </div>

            {/* Option 2: Upload signature image file (below the pad) */}
            <div className="mb-3">
              <input type="file" accept="image/png,image/jpeg,image/webp" ref={sigInputRef} className="hidden" onChange={(e) => e.target.files?.[0] && uploadImage("signature", e.target.files[0])} data-testid="profile-signature-file-input" />
              <Button variant="outline" size="sm" onClick={() => sigInputRef.current?.click()} data-testid="profile-signature-upload-button">
                <Upload className="h-4 w-4 mr-1.5" /> Upload signature image
              </Button>
              <p className="text-[11px] text-slate-500 mt-1.5 font-medium">PNG, JPG or WEBP · <span className="text-indigo-600 font-semibold">Max size: 1MB</span> (transparent background recommended)</p>
            </div>
          </div>
        </div>
      </Card>

      {/* Terms & conditions */}
      <Card className="p-6 border-slate-200">
        <h2 className="font-display text-xl font-semibold flex items-center gap-2"><ListOrdered className="h-5 w-5 text-indigo-600" /> Terms & conditions</h2>
        <p className="text-sm text-slate-500 mt-0.5">Printed at the bottom of every receipt with sequence numbers (1, 2, 3 …). Overrides the default notes when set.</p>
        <div className="mt-4 space-y-2">
          {form.terms.map((t, i) => (
            <div key={i} className="flex items-center gap-2">
              <span className="text-xs font-mono text-slate-500 w-6 shrink-0 text-right">{i + 1}.</span>
              <Input value={t} onChange={(e) => setTerm(i, e.target.value)} placeholder="e.g. Rent must be paid by the 5th of every month" data-testid={`profile-term-input-${i}`} />
              <button onClick={() => removeTerm(i)} className="h-9 w-9 shrink-0 flex items-center justify-center text-rose-500 hover:bg-rose-50 rounded-md" data-testid={`profile-term-remove-${i}`}>
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
        <Button variant="outline" size="sm" className="mt-3" onClick={addTerm} data-testid="profile-term-add-button">
          <Plus className="h-4 w-4 mr-1" /> Add term
        </Button>
        <Button size="sm" className="mt-3 ml-2 bg-indigo-600 hover:bg-indigo-700 text-white" onClick={saveTerms} disabled={busy} data-testid="profile-terms-save-button">
          {busy ? "Saving…" : "Save terms"}
        </Button>
      </Card>

      {/* Property details */}
      <Card className="p-6 border-slate-200">
        <div className="mb-5">
          <h2 className="font-display text-xl font-semibold flex items-center gap-2"><UserCog className="h-5 w-5 text-indigo-600" /> Property details</h2>
          <p className="text-sm text-slate-500 mt-0.5">These details appear on every receipt PDF and are read-only.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <F label="Property name"><Input value={form.property_name || ""} disabled className="bg-slate-50" data-testid="profile-pg-name-input" /></F>
          <F label="Owner's name">
            <div className="flex gap-2">
              <Input value={form.owner_name || ""} maxLength={40} onChange={(e) => setForm((f) => ({ ...f, owner_name: e.target.value }))} placeholder="e.g. Ravi Kumar" data-testid="profile-owner-name-input" />
              <Button variant="outline" disabled={busy} onClick={saveOwnerName} data-testid="profile-owner-name-save-button">Save</Button>
            </div>
            {ownerNameError(form.owner_name) && <p className="text-xs text-red-600 mt-1" data-testid="profile-owner-name-error">{ownerNameError(form.owner_name)}</p>}
          </F>
          <F label="Type of stay">
            <Select value={form.stay_type || "PG"} disabled>
              <SelectTrigger data-testid="profile-stay-type-select" className="bg-slate-50"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="PG">PG</SelectItem><SelectItem value="Hostel">Hostel</SelectItem>
                <SelectItem value="Inn">Inn</SelectItem><SelectItem value="Coliving">Coliving</SelectItem>
              </SelectContent>
            </Select>
          </F>
          <F label="Building No"><Input value={form.address_building_no || ""} disabled className="bg-slate-50" data-testid="profile-building-no-input" /></F>
          <F label="Road Number"><Input value={form.address_road_number || ""} disabled className="bg-slate-50" data-testid="profile-road-no-input" /></F>
          <F label="Street Name"><Input value={form.address_street || ""} disabled className="bg-slate-50" data-testid="profile-street-input" /></F>
          <F label="Area"><Input value={form.address_area || ""} disabled className="bg-slate-50" data-testid="profile-area-input" /></F>
          <F label="Landmark"><Input value={form.address_landmark || ""} disabled className="bg-slate-50" data-testid="profile-landmark-input" /></F>
          <F label="City/Town"><Input value={form.address_city || ""} disabled className="bg-slate-50" data-testid="profile-city-input" /></F>
          <F label="State"><Input value={form.address_state || ""} disabled className="bg-slate-50" data-testid="profile-state-input" /></F>
          <F label="Pincode"><Input value={form.address_pincode || ""} disabled className="bg-slate-50" data-testid="profile-pincode-input" /></F>
          <F label="Mobile (WhatsApp)"><Input value={form.primary_whatsapp || ""} disabled className="bg-slate-50" data-testid="profile-whatsapp-input" /></F>
          <F label="Alternate mobile"><Input value={form.alt_mobile || ""} disabled className="bg-slate-50" data-testid="profile-alt-mobile-input" /></F>
          <F label="GSTIN"><Input value={form.gstin || ""} disabled className="bg-slate-50" data-testid="profile-gstin-input" /></F>
          <F label="Year established"><Input type="number" value={form.year_established ?? ""} disabled className="bg-slate-50" data-testid="profile-year-input" /></F>
          <F label="Buildings / blocks"><Input type="number" value={form.num_buildings ?? ""} disabled className="bg-slate-50" data-testid="profile-buildings-input" /></F>
          <F label="Total rooms (1–99)"><Input type="number" value={form.total_rooms ?? ""} disabled className="bg-slate-50" data-testid="profile-total-rooms-input" /></F>
          <F label="Total beds (1–300)"><Input type="number" min={1} max={300} value={form.total_beds ?? ""} onChange={(e) => setForm((f) => ({ ...f, total_beds: e.target.value }))} data-testid="profile-total-beds-input" /></F>
          <F label="Available beds (0–50)">
            <div className="flex gap-2">
              <Input type="number" min={0} max={50} value={form.available_beds ?? ""} onChange={(e) => setForm((f) => ({ ...f, available_beds: e.target.value }))} data-testid="profile-available-beds-input" />
              <Button variant="outline" disabled={busy} onClick={saveBeds} data-testid="profile-beds-save-button">Save</Button>
            </div>
          </F>
          <F label="Accommodation type">
            <Select value={form.accommodation_type || "Co-ed"} disabled>
              <SelectTrigger data-testid="profile-accommodation-type-select" className="bg-slate-50"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Male">Male</SelectItem><SelectItem value="Female">Female</SelectItem><SelectItem value="Co-ed">Co-ed</SelectItem>
              </SelectContent>
            </Select>
          </F>
          <F label="Email (sign-in ID)"><Input value={owner?.primary_email || ""} disabled className="bg-slate-50" data-testid="profile-email-input" /></F>
        </div>
      </Card>
    </div>
  );
}

function SignaturePad({ onSave }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef(null);
  const [hasInk, setHasInk] = useState(false);

  const getPos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };
  const start = (e) => { drawing.current = true; last.current = getPos(e); e.preventDefault(); };
  const move = (e) => {
    if (!drawing.current) return;
    const ctx = canvasRef.current.getContext("2d");
    const p = getPos(e);
    ctx.strokeStyle = "#0F172A";
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
    setHasInk(true);
    e.preventDefault();
  };
  const end = () => { drawing.current = false; };
  const clear = () => {
    const c = canvasRef.current;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    setHasInk(false);
  };
  const save = () => {
    if (!hasInk) return toast.error("Please sign on the pad first");
    canvasRef.current.toBlob((b) => b && onSave(b), "image/png");
  };

  return (
    <div>
      <canvas
        ref={canvasRef}
        width={560}
        height={140}
        className="max-w-full rounded-lg border-2 border-dashed border-slate-300 bg-white touch-none cursor-crosshair"
        style={{ width: 560, height: 140 }}
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={end}
        onPointerLeave={end}
        data-testid="profile-signature-canvas"
      />
      <div className="flex gap-2 mt-2">
        <Button variant="outline" size="sm" onClick={clear} data-testid="profile-signature-clear-button">
          <Eraser className="h-4 w-4 mr-1" /> Clear
        </Button>
        <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white" onClick={save} data-testid="profile-signature-save-button">
          <PenLine className="h-4 w-4 mr-1" /> Save signature
        </Button>
      </div>
    </div>
  );
}

function F({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">{label}</Label>
      {children}
    </div>
  );
}
